import { FormEvent, useState } from "react"
import { useForm } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Radio, RadioGroup } from "@/components/ui/radio"
import { Input } from "@/components/ui/input"
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog"
import { pluralize } from "@/lib/relative-time"
import type { CommunityVocabulary } from "@/lib/project-status"

type PlanOption = {
  id: number
  display_name: string
  version: number
  published: boolean
  heated_sf: number | null
  room_count: number
  lots_count: number
}

// "Pick the plan — the takeoff comes with it." The PC never draws (spec §5C).
export function NewLotDialog({
  communityId,
  vocabulary,
  plans,
}: {
  communityId: number
  vocabulary: CommunityVocabulary
  plans: PlanOption[]
}) {
  const [open, setOpen] = useState(false)
  const published = plans.filter((p) => p.published)
  const form = useForm<{
    lot_number: string
    address: string
    plan_id: number | ""
    mirrored: boolean
  }>({ lot_number: "", address: "", plan_id: published[0]?.id ?? "", mirrored: false })

  const selected = published.find((p) => p.id === form.data.plan_id) ?? null
  const unit = vocabulary.unit.toLowerCase()

  const submit = (e: FormEvent) => {
    e.preventDefault()
    form.post(`/communities/${communityId}/lots`, {
      onSuccess: () => {
        form.reset()
        setOpen(false)
      },
    })
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-lot">New {unit}</Button>
      </DialogTrigger>
      <DialogContent>
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>New {unit}</DialogTitle>
            <DialogDescription>
              Pick the {vocabulary.model.toLowerCase()} — the takeoff comes with it.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="lot-number">{vocabulary.unit} #</label>
                <Input
                  id="lot-number"
                  value={form.data.lot_number}
                  onChange={(e) => form.setData("lot_number", e.target.value)}
                  placeholder="217"
                  className="mt-1"
                  autoFocus
                />
              </div>
              <div>
                <label htmlFor="lot-address">Address</label>
                <Input
                  id="lot-address"
                  value={form.data.address}
                  onChange={(e) => form.setData("address", e.target.value)}
                  placeholder="Optional"
                  className="mt-1"
                />
              </div>
            </div>
            {(() => {
              const baseError = form.errors.lot_number ?? (form.errors as Record<string, string>).base
              return baseError ? <p className="text-sm text-danger-display">{baseError}</p> : null
            })()}

            <fieldset>
              <legend>{vocabulary.model} from the library</legend>
              <RadioGroup className="mt-2 space-y-2">
                {published.map((plan) => (
                  <label
                    key={plan.id}
                    className={`flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2.5 font-normal text-ink-body ${
                      form.data.plan_id === plan.id
                        ? "border-accent bg-accent-faded"
                        : "border-hairline"
                    }`}
                  >
                    <Radio
                      name="plan"
                      checked={form.data.plan_id === plan.id}
                      onChange={() => form.setData("plan_id", plan.id)}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-ink-display">
                        {plan.display_name}
                      </span>
                      <span className="block text-xs text-ink-muted">
                        {[
                          plan.heated_sf ? `${plan.heated_sf.toLocaleString()} SF` : null,
                          plan.room_count ? pluralize(plan.room_count, "room") : null,
                          `used on ${plan.lots_count} ${vocabulary.units.toLowerCase()}`,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </span>
                    <span className="shrink-0 font-mono text-xs text-ink-muted">v{plan.version}</span>
                  </label>
                ))}
                <label
                  className={`flex cursor-pointer items-center gap-3 rounded-lg border border-dashed px-3 py-2.5 font-normal text-ink-body ${
                    form.data.plan_id === "" ? "border-accent bg-accent-faded" : "border-hairline"
                  }`}
                >
                  <Radio
                    name="plan"
                    checked={form.data.plan_id === ""}
                    onChange={() => form.setData("plan_id", "")}
                  />
                  <span className="text-sm">
                    Not in the library — start a blank takeoff
                  </span>
                </label>
              </RadioGroup>
              {form.errors.plan_id && (
                <p className="mt-1 text-sm text-danger-display">{form.errors.plan_id}</p>
              )}
            </fieldset>

            {selected && (
              <div className="rounded-lg border border-accent/40 bg-accent-faded px-3.5 py-3 text-sm">
                Copies <strong>{selected.display_name} (v{selected.version})</strong>
                {[
                  selected.room_count ? ` — ${pluralize(selected.room_count, "room")}` : "",
                  selected.heated_sf ? `, ${selected.heated_sf.toLocaleString()} SF` : "",
                ].join("")}
                , product per room and waste included. Edits here won&apos;t touch the master.
                <label className="mt-2.5 flex items-center gap-2 font-normal text-ink-body">
                  <Checkbox
                    checked={form.data.mirrored}
                    onChange={(e) => form.setData("mirrored", e.target.checked)}
                    data-testid="mirror-plan"
                  />
                  Mirror the {vocabulary.model.toLowerCase()}
                </label>
              </div>
            )}
          </div>

          <DialogFooter className="mt-6">
            <DialogClose asChild>
              <Button type="button" variant="secondary">Cancel</Button>
            </DialogClose>
            <Button type="submit" disabled={form.processing}>
              {selected ? `Create ${unit} & copy takeoff` : `Create ${unit}`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
